import React from "react";
import { SeoHead } from "@/components/seo/SeoHead";
import { PageContainer } from "@/components/ui/PageContainer";
import { ContentLayout } from "@/components/content/ContentLayout";
import { ContentTable } from "@/components/content/ContentTable";
import { MobileViewportWarning } from "@/components/ui/MobileViewportWarning";
import "@/styles/print/article-print.css";

const CanadianInvestmentAccountsGlossary: React.FC = () => {
  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title='Registered Investment "Wrappers" (Tax-Advantaged Plans)'
        description='These are the official CRA-recognized account structures that define how investments are held and taxed, not what the investments actually are. Each wrapper has its own purpose, contribution rules, withdrawal conditions, and regulatory intent.'
        canonicalPath="/glossary/registered-accounts"
      />

      {/* Hero */}
      <header className="border-b border-border/60 bg-slate-900 text-slate-50">
        <PageContainer center>
          <div className="py-8 md:py-10">
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Registered Tax-Advantaged Plans</h1>
            <p className="mt-2 text-slate-300 text-base md:text-lg">
              CRA-recognized plan types that change how your investments are taxed – not what you can hold.
            </p>
          </div>
        </PageContainer>
      </header>

      <main>
        <ContentLayout>
        <section className="prose prose-slate max-w-none py-8 prose-p:leading-7 prose-p:text-[15px]">
          {/* Table with shared mobile viewport warning */}
          <MobileViewportWarning
            threshold={640}
            title="Wide Table"
            message="This table is wider than your screen. Swipe/scroll horizontally to view all columns."
            className="not-prose mb-3"
          >
          <ContentTable tableClassName="glossary-table">
              <thead className="bg-muted/60">
                <tr>
                  <th className="text-left p-2 border font-semibold">Plan Name</th>
                  <th className="text-left p-2 border font-semibold">Abbrev.</th>
                  <th className="text-left p-2 border font-semibold">Primary Purpose / Use Case</th>
                  <th className="text-left p-2 border font-semibold">Notes & Key Features</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-2 border font-medium"><a className="underline text-primary" href="https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/rrsps-related-plans/registered-retirement-savings-plan-rrsp.html" target="_blank" rel="noopener noreferrer">Registered Retirement Savings Plan</a></td>
                  <td className="p-2 border">RRSP</td>
                  <td className="p-2 border">Long-term retirement savings and tax deferral</td>
                  <td className="p-2 border">Contributions are deductible against income; growth is tax-deferred until withdrawal. Must be converted to a RRIF or annuity by Dec 31 of the year the holder turns 71. Often used to hold mutual funds, ETFs, GICs, and alternatives via eligible platforms.</td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium"><a className="underline text-primary" href="https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/registered-retirement-income-fund-rrif.html" target="_blank" rel="noopener noreferrer">Registered Retirement Income Fund</a></td>
                  <td className="p-2 border">RRIF</td>
                  <td className="p-2 border">Income-drawing phase of RRSP assets after retirement</td>
                  <td className="p-2 border">Mandatory conversion of RRSPs; withdrawals are taxable and subject to minimum annual withdrawal rules. Keeps investments growing tax-deferred on remaining balance.</td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium"><a className="underline text-primary" href="https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/tax-free-savings-account.html" target="_blank" rel="noopener noreferrer">Tax-Free Savings Account</a></td>
                  <td className="p-2 border">TFSA</td>
                  <td className="p-2 border">Flexible, post-tax savings for any goal</td>
                  <td className="p-2 border">Contributions are not deductible, but all growth and withdrawals are tax-free. Room accumulates annually and unused space carries forward indefinitely. Can hold the same investments as an RRSP.</td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium">Locked-In Retirement Account</td>
                  <td className="p-2 border">LIRA</td>
                  <td className="p-2 border">Preservation of pension assets after leaving a job</td>
                  <td className="p-2 border">Receives transfers from a company pension (RPP) when an employee leaves. Funds are "locked-in" until retirement age; withdrawals only via a LIF or LRIF conversion. Jurisdiction-specific rules (federal or provincial).</td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium">Locked-In Retirement Savings Plan</td>
                  <td className="p-2 border">LRSP</td>
                  <td className="p-2 border">Similar to LIRA but sourced from an RRSP with locked-in pension funds</td>
                  <td className="p-2 border">Used in some provinces instead of LIRA terminology. Conversion and withdrawal rules mirror those of a LIF.</td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium">Life Income Fund</td>
                  <td className="p-2 border">LIF</td>
                  <td className="p-2 border">Provides retirement income from a LIRA/LRSP</td>
                  <td className="p-2 border">Functions like a RRIF for locked-in money. Annual minimum and maximum withdrawal limits; excess funds generally cannot be withdrawn except under hardship exemptions.</td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium">Locked-In Retirement Income Fund</td>
                  <td className="p-2 border">LRIF</td>
                  <td className="p-2 border">Older variant of LIF, still used in some jurisdictions</td>
                  <td className="p-2 border">Similar to LIF but with slightly different withdrawal calculation formulas; new plans typically use the LIF structure.</td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium">Prescribed Registered Retirement Income Fund</td>
                  <td className="p-2 border">PRIF</td>
                  <td className="p-2 border">Manitoba and Saskatchewan alternative to LIF</td>
                  <td className="p-2 border">Allows withdrawals above the normal maximums while maintaining tax-deferral on remaining assets. Useful for greater flexibility in provincial regimes that permit it.</td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium"><a className="underline text-primary" href="https://www.canada.ca/en/revenue-agency/services/tax/registered-plans-administrators/about-registered-pension-plans-rpps/about-registered-pension-plans-rpps.html" target="_blank" rel="noopener noreferrer">Registered Pension Plan</a></td>
                  <td className="p-2 border">RPP</td>
                  <td className="p-2 border">Employer-sponsored pension (defined benefit or defined contribution)</td>
                  <td className="p-2 border">Contributions often matched by employer; funds grow tax-deferred until retirement. Highly regulated under the Income Tax Act and pension standards acts.</td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium">Simplified Pension Plan / Voluntary Retirement Savings Plan</td>
                  <td className="p-2 border">SPP / VRSP</td>
                  <td className="p-2 border">Group retirement savings for small employers or individuals</td>
                  <td className="p-2 border">Low-cost pension alternative available mainly in Quebec, Manitoba, and Saskatchewan. Functions like a group RRSP with payroll deductions.</td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium"><a className="underline text-primary" href="https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/registered-education-savings-plans-resps.html" target="_blank" rel="noopener noreferrer">Registered Education Savings Plan</a></td>
                  <td className="p-2 border">RESP</td>
                  <td className="p-2 border">Saving for a child’s post-secondary education</td>
                  <td className="p-2 border">Contributions are not deductible, but growth is tax-deferred. The federal government adds Canada Education Savings Grant (CESG) and Canada Learning Bond (CLB) benefits. Withdrawals for education are taxed in the student’s lower income bracket.</td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium"><a className="underline text-primary" href="https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/registered-disability-savings-plan-rdsp.html" target="_blank" rel="noopener noreferrer">Registered Disability Savings Plan</a></td>
                  <td className="p-2 border">RDSP</td>
                  <td className="p-2 border">Long-term financial security for persons with disabilities</td>
                  <td className="p-2 border">Contributions are not deductible, but earnings and government grants/bonds grow tax-deferred. Withdrawals are partly taxable depending on contribution type. Highly regulated and subject to disability eligibility criteria.</td>
                </tr>
                <tr>
                  <td className="p-2 border font-medium"><a className="underline text-primary" href="https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/first-home-savings-account.html" target="_blank" rel="noopener noreferrer">First Home Savings Account</a></td>
                  <td className="p-2 border">FHSA</td>
                  <td className="p-2 border">Hybrid plan for first-time homebuyers</td>
                  <td className="p-2 border">Combines RRSP-style deduction with TFSA-style tax-free withdrawals when used to buy a qualifying first home. Annual and lifetime contribution limits apply. Must be closed after 15 years or at age 71 if unused.</td>
                </tr>
              </tbody>
          </ContentTable>
          </MobileViewportWarning>

          <hr className="my-8 border-border/60 not-prose" />

          <h3>Summary Notes</h3>
          <ul className="not-prose list-disc pl-5 space-y-1.5 text-[15px] leading-7">
            <li>All registered wrappers are <strong>defined by CRA legislation</strong> – they govern tax treatment, not investment selection.</li>
            <li>Investments inside can include <strong>mutual funds, ETFs, GICs, equities, bonds, and eligible alternative assets</strong>.</li>
            <li>Transfers between registered plans are <strong>non-taxable</strong> if done via direct transfer.</li>
            <li><strong>Contribution limits</strong> are individualized (CRA tracks room for RRSP, TFSA, FHSA).</li>
            <li><strong>Withdrawals and conversions</strong> have specific timing and eligibility rules depending on the wrapper type.</li>
          </ul>
        </section>
        </ContentLayout>
      </main>
    </div>
  );
};

export default CanadianInvestmentAccountsGlossary;
